import { STORAGE_KEY } from '../../shared/config';
import { useTitle } from '../../shared/hooks/useTitle';
import { getJSONItem } from '../../shared/services/storage/storage';
import { useNavigate } from 'react-router';
import { useContext, useState } from 'react';
import { AuthB2cContext } from '../context/AuthB2cContext';
import api from '../../shared/services/api/api';
import Loading from '../components/Loading';
import PageContainer from '../components/PageContainer';
import NavigationButtons from '../components/NavigationButtons';
import { useHotkeys } from 'react-hotkeys-hook';
import PageHeader from '../components/PageHeader';
import Button from '@mui/material/Button';
import {
  CARRIER_ROUTE,
  DATA_ENTRY_FIRST_STEP_ROUTE,
  RECAP_KEY,
  THANKS_KEY,
  createDataEntryRouteWithStep,
  createPathForRoute,
} from '../constants/baseRoutePaths';
import { pageSubtitles, pageTitles, testTypeCode } from '../constants/fields';
import { isCarrierFieldEnabled } from '../utils/isFieldEnabled';
import RecapStep from '../components/RecapStep';
import { preparePayloadForDatoVisita } from '../utils/preparePayloadForDatoVisita';
import { scrollToConf } from '../constants/scrollToConf';
import GeneralError from '../components/GeneralError';

const Recap = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [hided, setHided] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const state = getJSONItem(STORAGE_KEY.B2C_STATE, null);
  const navigate = useNavigate();
  const authB2cContext = useContext(AuthB2cContext);

  const title = pageTitles[RECAP_KEY];
  const subtitle = typeof pageSubtitles[RECAP_KEY] === 'string' ? pageSubtitles[RECAP_KEY] : '';

  useTitle(title);

  useHotkeys('enter', () => handleSendData());
  useHotkeys('shift+enter', () => navigate(createPathForRoute(DATA_ENTRY_FIRST_STEP_ROUTE)));

  const getKeysForStep = (step: number) => Object.keys(state).filter((key) => state[key]?.step === step);
  const step0 = getKeysForStep(0).map((key) => state[key]);
  const step1 = getKeysForStep(1).map((key) => state[key]);
  const step2 = getKeysForStep(2).map((key) => state[key]);
  const step3 = getKeysForStep(3).map((key) => state[key]);
  const step4 = getKeysForStep(4).map((key) => state[key]);
  const step5 = getKeysForStep(5).map((key) => state[key]);

  const handleSendData = async () => {
    const payload = preparePayloadForDatoVisita(state, testTypeCode);
    const parameters = {
      visitId: Number(authB2cContext?.user?.visitId),
      accessToken: authB2cContext?.user?.access_token || '',
      payload,
    };
    setLoading(true);
    const response = await api.visit(parameters).catch((error) => {
      console.error(error);
      setLoading(false);
      setError(true);
    });
    if (response.status === 200 && response.success === true) {
      setError(false);
      window.scrollTo(scrollToConf);
      navigate(createPathForRoute(isCarrierFieldEnabled(state) ? CARRIER_ROUTE : THANKS_KEY));
    }
    setLoading(false);
    setError(true);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <PageContainer>
      <>
        <PageHeader title={title} subtitle={subtitle} />
        <div className="flex justify-end xl:h-2">
          <Button
            onClick={(e) => {
              e.preventDefault();
              setHided(!hided);
            }}
          >
            Nascondi i non selezionati (N/A)
          </Button>
        </div>
        <div className="flex flex-wrap justify-between my-4">
          <RecapStep steps={step0} step={1} hided={hided} />
          <RecapStep steps={step1} step={2} hided={hided} />
          <RecapStep steps={step2} step={3} hided={hided} />
          <RecapStep steps={step3} step={4} hided={hided} />
          <RecapStep steps={step4} step={5} hided={hided} />
          <RecapStep steps={step5} step={6} hided={hided} />
        </div>
        {error && (
          <GeneralError
            message={"Si è verificato un errore al salvataggio dei dati. Riprovare o contattare l'assistenza tecnica."}
          />
        )}
        <NavigationButtons
          prev={{
            label: 'Modifica i dati',
            action: () => navigate(createPathForRoute(createDataEntryRouteWithStep(1))),
          }}
          next={{
            label: 'Conferma i dati',
            action: () => handleSendData(),
            color: 'success',
          }}
        />
      </>
    </PageContainer>
  );
};

export default Recap;
